import { useState, useEffect, useRef, useCallback } from 'react';

interface Category {
  id: string;
  [key: string]: unknown;
}

interface UseScrollDetectionOptions {
  /** Array of categories to track */
  categories: Category[];
  /** Ref to the navbar sentinel element (for sticky detection) */
  navSentinelRef: React.RefObject<HTMLDivElement | null>;
  /** Offset from top for category detection (px) */
  headerOffset?: number;
  /** Debounce delay for category changes (ms) */
  debounceMs?: number;
  /** Hysteresis for scroll direction changes (px) */
  hysteresisPx?: number;
  /** Scroll threshold to show scroll-to-top button (px) */
  scrollTopThreshold?: number;
}

interface UseScrollDetectionReturn {
  /** Currently active category ID (null = "All") */
  activeCategory: string | null;
  /** Whether the navbar is currently sticky */
  isNavbarSticky: boolean;
  /** Whether to show the scroll-to-top button */
  showScrollTop: boolean;
  /** Manually set active category (for programmatic navigation) */
  setActiveCategory: (id: string | null) => void;
  /** Ref to check if manual scroll is in progress */
  isManualScroll: React.MutableRefObject<boolean>;
  /** Clear pending category change */
  clearPendingCategory: () => void;
}

/**
 * Hook for scroll-based category detection with debouncing and hysteresis.
 * Tracks active category based on scroll position and handles sticky navbar detection.
 */
export function useScrollDetection({
  categories,
  navSentinelRef,
  headerOffset = 180,
  debounceMs = 100,
  hysteresisPx = 50,
  scrollTopThreshold = 600,
}: UseScrollDetectionOptions): UseScrollDetectionReturn {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isNavbarSticky, setIsNavbarSticky] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const isManualScroll = useRef(false);
  const pendingCategoryRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollY = useRef(0);

  const clearPendingCategory = useCallback(() => {
    pendingCategoryRef.current = null;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // 1. Show/Hide Scroll to top button
      setShowScrollTop(currentScrollY > scrollTopThreshold);

      // 2. Sticky Navbar Logic
      if (navSentinelRef.current) {
        const sentinelRect = navSentinelRef.current.getBoundingClientRect();
        setIsNavbarSticky(sentinelRect.bottom <= 0);
      }

      // 3. Active Category Detection (only if not scrolling manually)
      if (!isManualScroll.current && categories.length > 0) {
        let newActiveCategory: string | null = null;

        // If at the very top, show "All"
        if (currentScrollY < 100) {
          newActiveCategory = null;
        }
        // If at the very bottom, show last category
        else if (window.innerHeight + currentScrollY >= document.body.offsetHeight - 50) {
          const lastCat = categories[categories.length - 1];
          if (lastCat) newActiveCategory = lastCat.id;
        }
        // Normal scroll detection with hysteresis
        else {
          for (const category of categories) {
            const element = document.getElementById(`category-${category.id}`);
            if (element) {
              const rect = element.getBoundingClientRect();
              // Apply hysteresis: require element to be clearly in view
              const isScrollingDown = currentScrollY > lastScrollY.current;
              const threshold = isScrollingDown
                ? headerOffset - hysteresisPx
                : headerOffset + hysteresisPx;

              if (rect.top <= threshold && rect.bottom > headerOffset) {
                newActiveCategory = category.id;
                break;
              }
            }
          }
        }

        // Update last scroll position
        lastScrollY.current = currentScrollY;

        // Only update if the category actually changed (prevent unnecessary re-renders)
        if (newActiveCategory !== activeCategory) {
          // Use debouncing: only apply change if it persists
          if (pendingCategoryRef.current !== newActiveCategory) {
            pendingCategoryRef.current = newActiveCategory;

            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
              // Double-check the pending category is still what we want
              if (pendingCategoryRef.current === newActiveCategory) {
                setActiveCategory(newActiveCategory);
              }
            }, debounceMs);
          }
        }
      }
    };

    // Throttle scroll event for performance
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Initial check
    handleScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [categories, activeCategory, navSentinelRef, headerOffset, debounceMs, hysteresisPx, scrollTopThreshold]);

  return {
    activeCategory,
    isNavbarSticky,
    showScrollTop,
    setActiveCategory,
    isManualScroll,
    clearPendingCategory,
  };
}

export default useScrollDetection;
